import { wbAlsSvg, type WbElement } from '@beholder/shared/module/whiteboard';

export function bildAdresse(dateiId: string) {
  return `/api/dateien/${dateiId}?anzeigen=1`;
}

function dateiname(name: string, endung: string) {
  return `${name.replace(/[\\/:*?"<>|]+/g, '_').trim() || 'Tafel'}.${endung}`;
}

function herunterladen(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/** Bilder der Tafel als data:-Adressen einbetten, damit die Datei für sich allein funktioniert. */
async function eingebetteteBilder(els: WbElement[]): Promise<Map<string, string>> {
  const ids = [...new Set(els.filter((e) => e.art === 'bild' && e.dateiId && !e.geloescht).map((e) => e.dateiId!))];
  const karte = new Map<string, string>();
  await Promise.all(
    ids.map(async (id) => {
      try {
        const r = await fetch(bildAdresse(id), { credentials: 'same-origin' });
        if (!r.ok) return;
        const blob = await r.blob();
        const url = await new Promise<string>((ok, f) => {
          const leser = new FileReader();
          leser.onload = () => ok(String(leser.result));
          leser.onerror = () => f(leser.error);
          leser.readAsDataURL(blob);
        });
        karte.set(id, url);
      } catch {
        /* Bild fehlt – Platzhalter */
      }
    }),
  );
  return karte;
}

async function svgText(els: WbElement[], dunkel: boolean) {
  const bilder = await eingebetteteBilder(els);
  return wbAlsSvg(els, { bildAdresse: (id) => bilder.get(id) ?? null, rand: 32, dunkel });
}

export async function alsSvgSpeichern(name: string, els: WbElement[], dunkel = false) {
  const svg = await svgText(els, dunkel);
  herunterladen(new Blob([svg], { type: 'image/svg+xml' }), dateiname(name, 'svg'));
}

export async function alsPngSpeichern(name: string, els: WbElement[], dunkel = false) {
  const svg = await svgText(els, dunkel);
  const masse = /width="([\d.]+)" height="([\d.]+)"/.exec(svg);
  const b = Number(masse?.[1] ?? 800);
  const h = Number(masse?.[2] ?? 600);
  // Doppelte Auflösung, aber nicht größer als Browser-Leinwände erlauben
  const faktor = Math.min(2, 8000 / b, 8000 / h);
  const bild = new Image();
  const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
  try {
    await new Promise<void>((ok, f) => {
      bild.onload = () => ok();
      bild.onerror = () => f(new Error('Das Bild konnte nicht erzeugt werden.'));
      bild.src = url;
    });
    const leinwand = document.createElement('canvas');
    leinwand.width = Math.max(1, Math.round(b * faktor));
    leinwand.height = Math.max(1, Math.round(h * faktor));
    const ctx = leinwand.getContext('2d')!;
    ctx.drawImage(bild, 0, 0, leinwand.width, leinwand.height);
    const png = await new Promise<Blob | null>((ok) => leinwand.toBlob(ok, 'image/png'));
    if (!png) throw new Error('Das Bild konnte nicht erzeugt werden.');
    herunterladen(png, dateiname(name, 'png'));
  } finally {
    URL.revokeObjectURL(url);
  }
}
